import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress,
  TablePagination,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  Campaign as CampaignIcon,
  Analytics as AnalyticsIcon,
  AutoAwesome as AutoAwesomeIcon,
  Search as SearchIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import { Group, groupService } from '../../services/groupService';
import { format } from 'date-fns';

const GroupsList: React.FC = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);

  useEffect(() => {
    loadGroups();
  }, [page, rowsPerPage, searchTerm, statusFilter, typeFilter]);

  const loadGroups = async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {
        page: (page + 1).toString(),
        page_size: rowsPerPage.toString(),
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (statusFilter !== 'all') {
        params.is_active = statusFilter;
      }

      if (typeFilter !== 'all') {
        params.group_type = typeFilter;
      }

      const response = await groupService.getGroups(params);
      setGroups(response.results || []);
    } catch (err) {
      setError('Failed to load groups');
      console.error('Error loading groups:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!selectedGroup) return;

    try {
      await groupService.deleteGroup(selectedGroup.id);
      setSuccess('Group deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedGroup(null);
      loadGroups();
    } catch (err) {
      setError('Failed to delete group');
      console.error('Error deleting group:', err);
    }
  };

  const handleAutoPopulate = async (group: Group) => {
    if (group.group_type !== 'auto') return;

    try {
      setLoading(true);
      await groupService.autoPopulateGroup(group.id);
      setSuccess('Group auto-populated successfully');
      loadGroups();
    } catch (err) {
      setError('Failed to auto-populate group');
      console.error('Error auto-populating group:', err);
    } finally {
      setLoading(false);
    }
  };

  const getGroupTypeColor = (type: string) => {
    switch (type) {
      case 'manual':
        return 'primary';
      case 'auto':
        return 'secondary';
      case 'template':
        return 'success';
      default:
        return 'default';
    }
  };

  const getGroupTypeIcon = (type: string) => {
    switch (type) {
      case 'auto':
        return <AutoAwesomeIcon fontSize="small" />;
      default:
        return <GroupIcon fontSize="small" />;
    }
  };

  const filteredGroups = groups;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Customer Groups
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          href="/groups/create"
          sx={{ minWidth: 150 }}
        >
          Create Group
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search groups..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              label="Type"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="manual">Manual</MenuItem>
              <MenuItem value="auto">Automatic</MenuItem>
              <MenuItem value="template">Template</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Groups Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Members</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Campaign</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredGroups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No groups found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredGroups.map((group) => (
                <TableRow key={group.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getGroupTypeIcon(group.group_type)}
                      <Box>
                        <Typography variant="subtitle2">{group.name}</Typography>
                        {group.description && (
                          <Typography variant="body2" color="text.secondary">
                            {group.description.substring(0, 50)}
                            {group.description.length > 50 ? '...' : ''}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={group.group_type}
                      color={getGroupTypeColor(group.group_type) as any}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {group.member_count} members
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={group.is_active ? 'Active' : 'Inactive'}
                      color={group.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {group.last_campaign_sent ? (
                      <Typography variant="body2">
                        {format(new Date(group.last_campaign_sent), 'MMM dd, yyyy')}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Never
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {format(new Date(group.created_at), 'MMM dd, yyyy')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      by {group.created_by_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          href={`/groups/${group.id}`}
                        >
                          <GroupIcon />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Analytics">
                        <IconButton
                          size="small"
                          href={`/groups/${group.id}/analytics`}
                        >
                          <AnalyticsIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Create Campaign">
                        <IconButton
                          size="small"
                          href={`/campaigns/create?group=${group.id}`}
                        >
                          <CampaignIcon />
                        </IconButton>
                      </Tooltip>

                      {group.group_type === 'auto' && (
                        <Tooltip title="Auto Populate">
                          <IconButton
                            size="small"
                            onClick={() => handleAutoPopulate(group)}
                          >
                            <PlayArrowIcon />
                          </IconButton>
                        </Tooltip>
                      )}

                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          href={`/groups/${group.id}/edit`}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedGroup(group);
                            setDeleteDialogOpen(true);
                          }}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        
        <TablePagination
          component="div"
          count={-1} // We don't have total count from API
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Group</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the group "{selectedGroup?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteGroup} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbars */}
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GroupsList; 